import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

interface DailyTrendData { date: string; metres: number; }
interface PenetrationTrendData { date: string; rate: number; }
interface DelayBreakdownData { name: string; value: number; }
interface RigMetresData { rig: string; metres: number; }
interface RigUtilisationData {
  rig: string;
  metres: number;
  shifts: number;
  avgDelayMins: number;
  availability: number;
}

interface BitManufacturerGroup {
  diameter: number;
  manufacturers: { manufacturer: string; avgPenRate: number; count: number }[];
}

interface HammerWearPoint { cumulativeMetres: number; penRate: number; }
interface HammerWearSeries { serial: string; size: string; curve: HammerWearPoint[]; }

interface DrillingChartsProps {
  dailyTrendData: DailyTrendData[];
  penetrationTrendData?: PenetrationTrendData[];
  delayBreakdown: DelayBreakdownData[];
  metresByRig?: RigMetresData[];
  rigUtilisationData?: RigUtilisationData[];
  hammerWearData?: HammerWearSeries[];
  bitManufacturerData?: BitManufacturerGroup[];
}

const tooltipStyle = {
  backgroundColor: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
};

export default function DrillingCharts({
  dailyTrendData,
  penetrationTrendData = [],
  delayBreakdown,
  metresByRig = [],
  rigUtilisationData = [],
  hammerWearData = [],
  bitManufacturerData = [],
}: DrillingChartsProps) {
  const WEAR_COLORS = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
    "hsl(var(--primary))",
    "#ef4444", "#f97316", "#22c55e", "#06b6d4", "#8b5cf6", "#ec4899",
  ];
  // Hammer wear: serial selection state + grouping by size
  const [selectedHammers, setSelectedHammers] = useState<string[]>([]);
  const hammerSizes = [...new Set(hammerWearData.map((h) => h.size))].sort();
  const toggleHammer = (serial: string) =>
    setSelectedHammers((prev) =>
      prev.includes(serial) ? prev.filter((s) => s !== serial) : [...prev, serial]
    );
  return (
    <div className="space-y-6 mb-6">
      {/* Row 1 — Daily Metres Trend + Penetration Rate Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Daily Drilling Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dailyTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} unit="m" />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${Number(v).toLocaleString()}m`, "Metres"]} />
                <Line type="monotone" dataKey="metres" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Penetration Rate Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {penetrationTrendData.length === 0 ? (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-sm text-muted-foreground">No data</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={penetrationTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} unit=" m/hr" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} m/hr`, "Penetration Rate"]} />
                  <Line type="monotone" dataKey="rate" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2 — Metres by Rig + Delay Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Metres by Drill Asset</CardTitle>
          </CardHeader>
          <CardContent>
            {metresByRig.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(300, metresByRig.length * 32)}>
                <BarChart data={metresByRig} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} unit="m" />
                  <YAxis dataKey="rig" type="category" stroke="hsl(var(--muted-foreground))" fontSize={11} width={100} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${Number(v).toLocaleString()}m`, "Metres"]} />
                  <Bar dataKey="metres" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Delay Breakdown (minutes)</CardTitle>
          </CardHeader>
          <CardContent>
            {delayBreakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No delays recorded</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(300, delayBreakdown.length * 40)}>
                <BarChart data={delayBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={110} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} min`, "Duration"]} />
                  <Bar dataKey="value" fill="hsl(var(--chart-3))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Rig Utilisation Table */}
      {rigUtilisationData.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Rig Utilisation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Rig</th>
                    <th className="pb-2 font-medium text-right">Shifts</th>
                    <th className="pb-2 font-medium text-right">Total Metres</th>
                    <th className="pb-2 font-medium text-right">Avg m / Shift</th>
                    <th className="pb-2 font-medium text-right">Avg Delay</th>
                    <th className="pb-2 font-medium text-right">Availability</th>
                  </tr>
                </thead>
                <tbody>
                  {rigUtilisationData.map((row) => (
                    <tr key={row.rig} className="border-b border-border/50 last:border-0">
                      <td className="py-2 font-medium text-foreground">{row.rig}</td>
                      <td className="py-2 text-right text-muted-foreground">{row.shifts}</td>
                      <td className="py-2 text-right text-foreground">{row.metres.toLocaleString()}m</td>
                      <td className="py-2 text-right text-foreground">
                        {row.shifts > 0 ? `${Math.round(row.metres / row.shifts).toLocaleString()}m` : "—"}
                      </td>
                      <td className="py-2 text-right text-muted-foreground">{row.avgDelayMins} min</td>
                      <td className="py-2 text-right">
                        <span className={`font-medium ${row.availability >= 60 ? "text-green-500" : row.availability >= 40 ? "text-yellow-500" : "text-red-500"}`}>
                          {row.availability}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bit Manufacturer Penetration Rate Comparison */}
      {bitManufacturerData.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Bit Manufacturer Penetration Rate Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {bitManufacturerData.map((group) => (
                <div key={group.diameter}>
                  <h4 className="text-sm font-medium text-foreground mb-3">{group.diameter}mm</h4>
                  <ResponsiveContainer width="100%" height={Math.max(160, group.manufacturers.length * 36)}>
                    <BarChart data={group.manufacturers} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} unit=" m/hr" />
                      <YAxis dataKey="manufacturer" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={80} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, props) => [`${v} m/hr (${props.payload.count} bits)`, "Avg Pen Rate"]} />
                      <Bar dataKey="avgPenRate" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}



      {/* Hammer Wear Curves — Pen Rate vs Cumulative Metres */}
      {hammerWearData.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-foreground">Hammer Wear Curves — Penetration Rate vs Life</CardTitle>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 max-w-xs">
                  <span className="truncate">
                    {selectedHammers.length === 0
                      ? "Select Hammers"
                      : selectedHammers.join(", ")}
                  </span>
                  <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 max-h-80 overflow-y-auto p-2 z-50" align="end">
                {selectedHammers.length > 0 && (
                  <button
                    className="w-full text-left text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground mb-1"
                    onClick={() => setSelectedHammers([])}
                  >
                    Clear selection
                  </button>
                )}
                {hammerSizes.map((size) => {
                  const serials = hammerWearData.filter((h) => h.size === size);
                  return (
                    <div key={size}>
                      <p className="text-xs font-medium text-muted-foreground px-2 pt-2 pb-1">{size}</p>
                      {serials.map((s) => {
                        const checked = selectedHammers.includes(s.serial);
                        return (
                          <button
                            key={s.serial}
                            className="flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent"
                            onClick={() => toggleHammer(s.serial)}
                          >
                            <Check className={cn("h-4 w-4 shrink-0", checked ? "opacity-100" : "opacity-0")} />
                            {s.serial}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </PopoverContent>
            </Popover>
          </CardHeader>
          <CardContent>
            {selectedHammers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Select one or more hammers to view wear curves.</p>
            ) : (
            <div className="space-y-6">
              {hammerSizes.map((size) => {
                const allSeries = hammerWearData.filter((h) => h.size === size);
                const series = allSeries.filter((h) => selectedHammers.includes(h.serial));
                if (series.length === 0) return null;
                const allPoints: Record<number, Record<string, number>> = {};
                series.forEach((s) => {
                  s.curve.forEach((pt) => {
                    if (!allPoints[pt.cumulativeMetres]) allPoints[pt.cumulativeMetres] = {};
                    allPoints[pt.cumulativeMetres][s.serial] = pt.penRate;
                  });
                });
                const chartData = Object.entries(allPoints)
                  .map(([m, vals]) => ({ metres: Number(m), ...vals }))
                  .sort((a, b) => a.metres - b.metres);

                return (
                  <div key={size}>
                    <h4 className="text-sm font-medium text-foreground mb-3">{size} ({series.length} of {allSeries.length} units)</h4>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={chartData} margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="metres"
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={11}
                          tickFormatter={(v) => `${Number(v).toLocaleString()}m`}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          unit=" m/hr"
                        />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          labelFormatter={(v) => `${Number(v).toLocaleString()}m cumulative`}
                          formatter={(v) => [`${v} m/hr`]}
                        />
                        {series.map((s, i) => (
                          <Line
                            key={s.serial}
                            dataKey={s.serial}
                            type="monotone"
                            stroke={WEAR_COLORS[i % WEAR_COLORS.length]}
                            strokeWidth={1.5}
                            dot={false}
                            name={s.serial}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
